import React, { useState, useRef, useCallback, useEffect, useMemo } from "react"
import { GoArrowRight } from "react-icons/go"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { FiSearch, FiUser, FiMapPin, FiUserPlus } from "react-icons/fi"
import Header from "../../components/Header"
import { WR } from "../../components/ui/tokens"
import ApiRequest from "../../auth/apiRequestHelper"
import {
  setSelectedItemsClear,
  setSelectedUserId,
  setUserName,
  setWoshUserId,
  setSelectedUserMonthlyInvoice,
  resetSelectedUserBilling,
} from "../../store/slices/cartSlice"
import Loader from "react-loader-spinner"
import { useTranslation } from "react-i18next"

const MIN_SEARCH_LENGTH = 3
const DEBOUNCE_MS = 400

const Start = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isRetrieve = location.state?.isRetrieve
  const selectedUserId = useSelector(
    (state) => state.cart.userInfo?.selectedUserId
  )

  const [searchTerm, setSearchTerm] = useState("")
  // Delivery-address search field — shown on both new-order and retrieve
  // flows. Useful when staff knows the address but not the exact customer
  // name (e.g. delivery person at the door, or a return customer giving
  // their address). Both fields combine into a single API call when either
  // has typed text.
  const [addressTerm, setAddressTerm] = useState("")
  const [userData, setUserData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const debounceTimerRef = useRef(null)
  const controllerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      if (controllerRef.current) controllerRef.current.abort()
    }
  }, [])

  const accent = isRetrieve ? WR.color.mintDeep : WR.color.brand
  // Retrieve (OUT) flow gets the mint accent on every tinted surface.
  const softBg = isRetrieve ? WR.color.accentSoft : WR.color.lavSoft
  const eyebrowBg = isRetrieve ? WR.color.accentSoft : "rgba(61, 69, 134, 0.08)"
  const idBadgeBg = isRetrieve ? WR.color.accentSoft : WR.color.cardMuted
  const idBadgeColor = isRetrieve ? WR.color.mintDeep : WR.color.brand
  const flowLabel = isRetrieve ? t("DIRECTION_OUT") : t("DIRECTION_IN")
  const modeLabel = isRetrieve ? t("RETRIEVE") : t("NEW_ORDER")
  const pageTitle = isRetrieve ? t("START_RETRIEVE_TITLE") : t("START_NEW_ORDER_TITLE")
  const pageSub = isRetrieve ? t("START_RETRIEVE_SUB") : t("START_NEW_ORDER_SUB")
  const inputRow = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    height: 56,
    padding: "0 16px",
    borderRadius: 18,
    background: "#fff",
    border: `1.5px solid ${WR.color.border}`,
    marginBottom: 16,
  }
  const fieldLabel = {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: WR.color.inkMuted,
    marginBottom: 7,
  }
  const inputStyle = {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: 15,
    fontWeight: 600,
    color: WR.color.encre,
    background: "transparent",
    fontFamily: WR.font.sans,
  }
  const resultBtn = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "15px 16px",
    borderRadius: 18,
    background: "#fff",
    border: `1px solid ${WR.color.border}`,
    cursor: "pointer",
  }

  const executeSearch = useCallback(async ({ q, address }) => {
    if (controllerRef.current) {
      controllerRef.current.abort()
    }

    controllerRef.current = new AbortController()
    setIsLoading(true)
    setUserData([])

    const params = []
    if (q) params.push(`q=${encodeURIComponent(q)}`)
    if (address) params.push(`address=${encodeURIComponent(address)}`)

    try {
      const response = await ApiRequest.userFind(
        `shop/users?${params.join("&")}`,
        controllerRef.current.signal
      )
      setUserData(response.data.users)
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(
          "Error finding user:",
          error?.response?.data ?? error.message
        )
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Combine both search fields into one debounced API call. Fires when at
  // least one of them has >= MIN_SEARCH_LENGTH chars; the other gets sent
  // along when it also crosses the threshold.
  const triggerSearch = useCallback(
    (nextSearchTerm, nextAddressTerm) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      if (controllerRef.current) controllerRef.current.abort()

      const qReady = nextSearchTerm.trim().length >= MIN_SEARCH_LENGTH
      const addressReady = nextAddressTerm.trim().length >= MIN_SEARCH_LENGTH
      if (!qReady && !addressReady) {
        setUserData(null)
        setIsLoading(false)
        return
      }

      debounceTimerRef.current = setTimeout(() => {
        executeSearch({
          q: qReady ? nextSearchTerm.trim() : null,
          address: addressReady ? nextAddressTerm.trim() : null,
        })
      }, DEBOUNCE_MS)
    },
    [executeSearch]
  )

  const handleInputChange = useCallback(
    (value) => {
      setSearchTerm(value)
      triggerSearch(value, addressTerm)
    },
    [triggerSearch, addressTerm]
  )

  const handleAddressChange = useCallback(
    (value) => {
      setAddressTerm(value)
      triggerSearch(searchTerm, value)
    },
    [triggerSearch, searchTerm]
  )

  // Defensive client-side filter: the backend's address-matching is too
  // broad (e.g. it returns users whose historical orders touched the
  // address, even if their current delivery_address is something else,
  // and sometimes users with no displayed address at all). When the
  // address field is the one driving the search, keep only users whose
  // currently-displayed delivery_address actually contains the typed term.
  // This eliminates false positives without depending on a backend change.
  const filteredUserData = useMemo(() => {
    if (!userData) return userData
    const a = addressTerm.trim().toLowerCase()
    if (a.length < MIN_SEARCH_LENGTH) return userData
    return userData.filter((u) =>
      (u.delivery_address || "").toLowerCase().includes(a)
    )
  }, [userData, addressTerm])

  const handleUserSelect = (user) => {
    if (selectedUserId !== user.id) {
      dispatch(setSelectedItemsClear())
    }
    // Purge the previous customer's contact/billing fields BEFORE selecting:
    // they are hydrated lazily and stale values leak into PEPPOL invoices
    // and email-ticket checks otherwise.
    dispatch(resetSelectedUserBilling())
    dispatch(setSelectedUserId(user.id))
    dispatch(setWoshUserId(user.wosh_user_id))
    dispatch(setUserName(user.name))
    dispatch(setSelectedUserMonthlyInvoice(user.monthly_invoice))
    navigate(isRetrieve ? "/retrivecheckout" : "/")
  }

  return (
    <>
      <Header />
      <div
        style={{
          background: WR.color.bg,
          minHeight: "calc(100vh - 67px)",
          fontFamily: WR.font.sans,
          padding: "0 clamp(16px,4vw,40px)",
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            width: "100%",
            minHeight: "calc(100vh - 67px)",
            display: "flex",
            alignItems: "center",
            gap: 28,
          }}
        >
          {/* LEFT — eyebrow, title, subtitle, search card. Vertically centered
              and FIXED: it never shifts when results load on the right. */}
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "6px 13px",
              borderRadius: 999,
              background: eyebrowBg,
              color: accent,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 999, background: accent }} />
            {flowLabel} · {modeLabel}
          </div>
          <h1
            style={{
              fontSize: "clamp(26px,3.2vw,34px)",
              fontWeight: 800,
              letterSpacing: -0.6,
              margin: "0 0 6px",
              color: WR.color.encre,
            }}
          >
            {pageTitle}
          </h1>
          <p style={{ fontSize: 15, color: WR.color.inkMuted, margin: "0 0 28px", maxWidth: 560 }}>
            {pageSub}
          </p>

          {/* search card */}
          <div
            style={{
              background: WR.color.surface,
              borderRadius: 24,
              boxShadow: WR.shadow.card,
              padding: 26,
            }}
          >
              <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    background: softBg,
                    color: accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiSearch size={21} />
                </div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: -0.2, color: WR.color.encre }}>
                    {t("FIND_USER")}
                  </div>
                  <div style={{ fontSize: 13, color: WR.color.inkMuted, marginTop: 2 }}>
                    {t("FIND_USER_SUB")}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label htmlFor="searchTerm" style={fieldLabel}>{t("FIND_USER_ID_LABEL")}</label>
                <div style={{ ...inputRow, marginBottom: 0 }}>
                  <FiUser size={18} color={WR.color.inkSoft} />
                  <input
                    id="searchTerm"
                    type="text"
                    placeholder={t("SEARCH_PLACEHOLDER")}
                    value={searchTerm}
                    onChange={(e) => handleInputChange(e.target.value)}
                    autoComplete="off"
                    style={inputStyle}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="addressTerm" style={fieldLabel}>{t("DELIVERY_ADDRESS")}</label>
                <div style={{ ...inputRow, marginBottom: 0 }}>
                  <FiMapPin size={18} color={WR.color.inkSoft} />
                  <input
                    id="addressTerm"
                    type="text"
                    placeholder={t("SEARCH_PLACEHOLDER_ADDRESS")}
                    value={addressTerm}
                    onChange={(e) => handleAddressChange(e.target.value)}
                    autoComplete="off"
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — create + results. Grows and scrolls on its own, centered,
              without ever shifting the left column. */}
          <div
            style={{
              flex: "1.05 1 0",
              minWidth: 0,
              alignSelf: "center",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              maxHeight: "calc(100vh - 67px - 48px)",
              overflowY: "auto",
              paddingRight: 4,
            }}
          >
              {!isRetrieve && (
                <Link to="/personalinfo" state={{ userName: searchTerm }} style={{ textDecoration: "none" }}>
                  <button
                    style={{
                      width: "100%",
                      height: 56,
                      borderRadius: 18,
                      background: accent,
                      color: "#fff",
                      fontSize: 15,
                      fontWeight: 700,
                      boxShadow: WR.shadow.cta,
                      border: 0,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 9,
                      fontFamily: WR.font.sans,
                    }}
                  >
                    <FiUserPlus size={19} />
                    {t("CREATE_NEW_USER")}
                  </button>
                </Link>
              )}

              {isLoading && (
                <div className="w-full d-flex justify-content-center">
                  <Loader type="Oval" color="#34A6AC" height={40} width={40} />
                </div>
              )}

              {filteredUserData?.map((user) => (
                <button key={user.id} style={resultBtn} onClick={() => handleUserSelect(user)}>
                  <div
                    style={{
                      height: 42,
                      minWidth: 42,
                      padding: "0 11px",
                      borderRadius: 12,
                      background: idBadgeBg,
                      color: idBadgeColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13.5,
                      fontWeight: 700,
                      letterSpacing: 0.2,
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {user?.wosh_user_id}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: WR.color.encre }}>
                      {user?.first_name} {user?.last_name}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: WR.color.inkMuted,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {user?.delivery_address}
                    </div>
                  </div>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 999,
                      background: softBg,
                      color: accent,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <GoArrowRight size={17} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
    </>
  )
}

export default Start

